import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SchoolForm } from '../../models/form';
import { AGE_GROUPS, AGE_GROUPS_SORTED } from '../../consts/school';
import { AppUser } from 'Src/views/App/App';
import { checkAvailabilityFormName } from '../../helpers/service/admin/forms';

interface Props {
  isOpen: boolean;
  onCloseClick: () => void;
  onFormCreated: (data: any) => void;
  form?: SchoolForm;
  user: AppUser;
}

interface State {
  nameForm: string;
  ageGroup: string;
}

interface AgeGroupSection {
  label: string;
  ageGroups: string[];
}

const getFormikErrorText = (error: unknown): string => (typeof error === 'string' ? error : '');

export class SchoolFormForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const isFormExists = this.isFormExist();

    const { form } = this.props;

    this.state = {
      nameForm: isFormExists ? propz.get(form, ['name'], '') : '',
      ageGroup: isFormExists ? propz.get(form, ['ageGroup'], '') : ''
    };
  }

  isFormExist(): boolean {
    const { form } = this.props;

    return typeof form !== 'undefined';
  }

  onSubmit = data => {
    this.props.onFormCreated(data);
  };

  getAgeGroup() {
    const user = this.props.user;
    const activeSchool = user.activeSchool;
    const ageGroupsNaming = activeSchool.ageGroupsNaming;
    const ageGroups = propz.get(AGE_GROUPS_SORTED, [ageGroupsNaming]);

    return ageGroups || [];
  }

  getAgeGroupSections(): AgeGroupSection[] {
    const ageGroups = this.getAgeGroup();
    const ageGroupsNaming = propz.get(this.props.user, ['activeSchool', 'ageGroupsNaming']);

    if (ageGroupsNaming !== 'ENGLISH') {
      return [{ label: 'Age groups', ageGroups }];
    }

    const definitions = [
      { label: 'Early years', values: ['N1', 'N2', 'Reception'] },
      { label: 'KS1', values: ['Y1', 'Y2'] },
      { label: 'KS2', values: ['Y3', 'Y4', 'Y5', 'Y6'] },
      { label: 'KS3', values: ['Y7', 'Y8', 'Y9'] },
      { label: 'KS4', values: ['Y10', 'Y11'] },
      { label: 'KS5', values: ['Y12', 'Y13'] },
      { label: 'Further education', values: ['Y14', 'Post-19'] }
    ];

    return definitions
      .map(definition => ({
        label: definition.label,
        ageGroups: definition.values.filter(value => ageGroups.indexOf(value) !== -1)
      }))
      .filter(section => section.ageGroups.length > 0);
  }

  render() {
    const isFormExists = this.isFormExist();
    const positiveButtonTitle = isFormExists ? 'Save registration form' : 'Create registration form';
    const { user, form } = this.props;

    const FormValidationSchema = Yup.object().shape({
      name: Yup.string()
        .required('Required')
        .test(
          'name',
          'You already have a form group matching this name assigned to the year group. ' +
            'Please change the name or select a different year group.',
          function(value) {
            const ageGroup = propz.get(this.parent, ['ageGroup']);
            const activeSchool = user.activeSchool;
            const ageGroupsNaming = activeSchool.ageGroupsNaming;
            const ageGroups = propz.get(AGE_GROUPS, [ageGroupsNaming]);
            const age = ageGroups.findIndex(group => group === ageGroup);
            const isExistAge = age !== -1;
            const currentName = propz.get(form, ['name'], '');
            const currentAgeGroup = propz.get(form, ['ageGroup'], '');
            const isSameForm = isFormExists && currentName === value && currentAgeGroup === ageGroup;
            switch (true) {
              case isSameForm:
                return true;
              case !isExistAge:
                return false;
              case !value:
                return false;
              default:
                return checkAvailabilityFormName(user, { name: value.trim(), age: age }).then(res => {
                  return res.isAvailable;
                });
            }
          }
        ),
      ageGroup: Yup.string()
        .oneOf(this.getAgeGroup(), 'Select a valid year group')
        .required('Required')
    });

    return (
      <Formik
        onSubmit={this.onSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{
          name: this.state.nameForm,
          ageGroup: this.state.ageGroup
        }}
        validationSchema={FormValidationSchema}
      >
        {({ touched, errors, values, setFieldValue, setFieldTouched }) => (
          <Form className="schoolFormForm schoolFormForm--ds">
            <div className="schoolFormForm__grid">
              <div className="form-group schoolFormForm__field">
                <label>Registration form name</label>
                <Field
                  type="text"
                  name="name"
                  className={`${touched.name && errors.name ? 'form-control is-invalid' : 'form-control '}`}
                  placeholder="Enter registration form name"
                />
                {touched.name && errors.name ? (
                  <div className="invalid-feedback">{getFormikErrorText(errors.name)}</div>
                ) : null}
              </div>
              <div className="form-group schoolFormForm__field">
                <label>Year group</label>
                <div
                  className={`schoolFormForm__ageGroups${touched.ageGroup && errors.ageGroup ? ' is-invalid' : ''}`}
                  role="radiogroup"
                  aria-label="Year group"
                >
                  {this.getAgeGroupSections().map(section => (
                    <section className="schoolFormForm__ageSection" key={section.label}>
                      <strong>{section.label}</strong>
                      <div className="schoolFormForm__ageChips">
                        {section.ageGroups.map(ageGroup => (
                          <button
                            type="button"
                            key={ageGroup}
                            role="radio"
                            aria-checked={values.ageGroup === ageGroup}
                            className={`schoolFormForm__ageChip${
                              values.ageGroup === ageGroup ? ' schoolFormForm__ageChip--selected' : ''
                            }`}
                            onClick={() => {
                              setFieldValue('ageGroup', ageGroup);
                              setFieldTouched('ageGroup', true, false);
                            }}
                          >
                            {ageGroup}
                          </button>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
                {touched.ageGroup && errors.ageGroup ? (
                  <div className="invalid-feedback">{getFormikErrorText(errors.ageGroup)}</div>
                ) : null}
              </div>
            </div>
            <div className="schoolFormForm__actions">
              <button
                type="button"
                className="schoolStructure-btn schoolStructure-btn--secondary"
                onClick={this.props.onCloseClick}
              >
                Cancel
              </button>
              <button type="submit" className="schoolStructure-btn schoolStructure-btn--primary">
                {positiveButtonTitle}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}
